import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { QrCode, Wallet, AlertTriangle, CheckCircle2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { AutopaySettingsSection } from "@/components/AutopaySettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DuplicatePaymentsCard = () => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/payments/duplicates`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { load(); }, []);
  const fix = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/dedupe`);
      toast.success(`Removed ${data.removed} duplicate payment record${data.removed === 1 ? "" : "s"}; balances recalculated`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove duplicates");
    } finally {
      setBusy(false);
    }
  };
  if (!info) return null;
  const ok = info.suspicious.length === 0;
  return (
    <div className={`rounded-xl border p-4 ${ok ? "border-green-200 bg-green-50" : "border-amber-300 bg-amber-50"}`} data-testid="duplicate-payments-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          {ok ? <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />}
          <div>
            <p className={`font-semibold ${ok ? "text-green-800" : "text-amber-900"}`} data-testid="duplicate-payments-status">
              {ok ? "Payment records are consistent" : `${info.suspicious.length} invoice${info.suspicious.length === 1 ? " has" : "s have"} duplicated payment records (${money(info.excess_total)} over-counted)`}
            </p>
            <p className="text-xs text-slate-600">Checks every invoice for payments exceeding its total (e.g. duplicated migration records that inflate "Collected").</p>
            {!ok && (
              <ul className="mt-2 text-xs text-amber-900 space-y-0.5" data-testid="duplicate-payments-list">
                {info.suspicious.slice(0, 5).map((s) => <li key={s.invoice_id}>{s.invoice_number} · {s.customer_name}: {s.payments} payments = {money(s.recorded)} vs invoice {money(s.invoice_total)}</li>)}
                {info.suspicious.length > 5 && <li>+{info.suspicious.length - 5} more</li>}
              </ul>
            )}
          </div>
        </div>
        {!ok && <Button size="sm" onClick={fix} disabled={busy} className="bg-amber-600 hover:bg-amber-700 shrink-0" data-testid="fix-duplicate-payments-button">{busy ? "Fixing…" : "Remove duplicates"}</Button>}
      </div>
    </div>
  );
};

const QrPreview = ({ method, params, enabled, testId }) => {
  const [src, setSrc] = useState(null);
  const query = new URLSearchParams({ method, ...params }).toString();
  useEffect(() => {
    if (!enabled) return undefined;
    let url;
    const timer = setTimeout(() => {
      axios.get(`${API}/settings/payment-qr?${query}`, { responseType: "blob" }).then((r) => {
        url = URL.createObjectURL(r.data);
        setSrc(url);
      }).catch(() => setSrc(null));
    }, 350);
    return () => { clearTimeout(timer); if (url) URL.revokeObjectURL(url); };
  }, [query, enabled]);
  if (!enabled) return <div className="w-36 h-36 rounded-lg bg-slate-100 flex items-center justify-center text-xs text-slate-400" data-testid={`${testId}-off`}>Disabled</div>;
  return src ? <img src={src} alt={`${method} QR preview`} className="w-36 h-36 rounded-lg border border-slate-200" data-testid={testId} /> : <div className="w-36 h-36 rounded-lg bg-slate-100 animate-pulse" />;
};

const MethodCard = ({ title, color, enabled, onToggle, toggleId, children, preview }) => (
  <div className={`rounded-xl border-2 p-5 ${enabled ? "" : "opacity-70"}`} style={{ borderColor: color }}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-semibold text-slate-800 flex items-center gap-2"><QrCode className="w-4 h-4" style={{ color }} />{title}</h3>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">{enabled ? "Shown on invoices" : "Hidden"}</span>
        <Switch checked={enabled} onCheckedChange={onToggle} data-testid={toggleId} />
      </div>
    </div>
    <div className="flex flex-col sm:flex-row gap-5">
      <div className="flex-1 space-y-3">{children}</div>
      <div className="shrink-0 flex flex-col items-center gap-1">{preview}<p className="text-xs text-slate-400">Live preview</p></div>
    </div>
  </div>
);

export const PaymentSettings = ({ settings, onChange }) => {
  const set = (patch) => onChange({ ...settings, ...patch });
  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="payment-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Wallet className="w-6 h-6 text-green-600" />Payment Options</CardTitle>
        <p className="text-sm text-slate-600 mt-2">These QR codes appear on unpaid invoice PDFs, invoice emails and customer statement emails. Switch a method off to hide it. Click "Save Settings" below to apply.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <DuplicatePaymentsCard />
        <div className="flex items-center justify-between rounded-xl border border-slate-200 p-4" data-testid="payment-receipts-setting">
          <div>
            <p className="font-semibold text-slate-800">Payment receipts</p>
            <p className="text-xs text-slate-500">Email the customer a branded "Payment received – thank you" message (with the updated invoice PDF) whenever a payment is recorded. Can also be skipped per payment.</p>
          </div>
          <Switch checked={settings.payment_receipts_enabled !== false} onCheckedChange={(v) => set({ payment_receipts_enabled: v })} data-testid="payment-receipts-toggle" />
        </div>
        <div className="rounded-xl border-2 border-slate-700 p-5" data-testid="card-payment-setting">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2"><CreditCard className="w-4 h-4" />Card payments (Stripe) on the View &amp; Pay page</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{settings.stripe_configured ? (settings.stripe_pay_page_enabled !== false ? "Offered to customers" : "Hidden") : "Add a Stripe key in Integrations first"}</span>
              <Switch checked={settings.stripe_pay_page_enabled !== false} disabled={!settings.stripe_configured} onCheckedChange={(v) => set({ stripe_pay_page_enabled: v })} data-testid="stripe-pay-page-toggle" />
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-3">Customers who choose to pay by card see the processing fee itemized before checkout and pay balance + fee. Cash App / Zelle stay fee-free.</p>
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <div>
              <Label htmlFor="stripe_fee_percent">Fee percent (%)</Label>
              <Input id="stripe_fee_percent" type="number" step="0.1" min="0" max="20" value={settings.stripe_fee_percent ?? 2.9} onChange={(e) => set({ stripe_fee_percent: e.target.value })} data-testid="stripe-fee-percent-input" />
            </div>
            <div>
              <Label htmlFor="stripe_fee_fixed">Fixed fee ($)</Label>
              <Input id="stripe_fee_fixed" type="number" step="0.01" min="0" max="20" value={settings.stripe_fee_fixed ?? 0.3} onChange={(e) => set({ stripe_fee_fixed: e.target.value })} data-testid="stripe-fee-fixed-input" />
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2" data-testid="stripe-fee-example">Example: a $100.00 balance is charged {money(100 + 100 * (parseFloat(settings.stripe_fee_percent ?? 2.9) || 0) / 100 + (parseFloat(settings.stripe_fee_fixed ?? 0.3) || 0))} by card.</p>
        </div>

        <AutopaySettingsSection />

        <MethodCard title="Cash App" color="#00D632" enabled={settings.cash_app_enabled !== false} onToggle={(v) => set({ cash_app_enabled: v })} toggleId="cash-app-enabled-toggle"
          preview={<QrPreview method="cash_app" params={{ cashtag: settings.cash_app_cashtag || "", amount: "125.00" }} enabled={settings.cash_app_enabled !== false} testId="cash-app-qr-preview" />}>
          <div>
            <Label htmlFor="cash_app_cashtag">Cashtag</Label>
            <div className="flex items-center gap-1">
              <span className="text-slate-500 font-semibold">$</span>
              <Input id="cash_app_cashtag" value={(settings.cash_app_cashtag || "").replace(/^\$/, "")} onChange={(e) => set({ cash_app_cashtag: e.target.value.replace(/^\$/, "") })} placeholder="cgnetworks" data-testid="cash-app-cashtag-input" />
            </div>
            <p className="text-xs text-slate-500 mt-1">Each invoice QR opens Cash App with the balance due prefilled (preview shows $125.00).</p>
          </div>
        </MethodCard>

        <MethodCard title="Zelle" color="#6D1ED4" enabled={settings.zelle_enabled !== false} onToggle={(v) => set({ zelle_enabled: v })} toggleId="zelle-enabled-toggle"
          preview={<QrPreview method="zelle" params={{ name: settings.zelle_name || "", token: settings.zelle_token || "" }} enabled={settings.zelle_enabled !== false} testId="zelle-qr-preview" />}>
          <div>
            <Label htmlFor="zelle_name">Recipient name (as registered with your bank)</Label>
            <Input id="zelle_name" value={settings.zelle_name || ""} onChange={(e) => set({ zelle_name: e.target.value })} placeholder="CG NETWORKS & CO LLC" data-testid="zelle-name-input" />
          </div>
          <div>
            <Label htmlFor="zelle_token">Zelle email or phone</Label>
            <Input id="zelle_token" value={settings.zelle_token || ""} onChange={(e) => set({ zelle_token: e.target.value })} placeholder="cgomez@cgnetworks.cloud" data-testid="zelle-token-input" />
            <p className="text-xs text-slate-500 mt-1">Zelle QR codes can't prefill an amount; invoices show "Send $X to …" next to the code.</p>
          </div>
        </MethodCard>
      </CardContent>
    </Card>
  );
};
